import { Box } from '@chakra-ui/react';
import React from 'react';

import type { TokenInfo } from 'types/api/token';

import config from 'configs/app';
import { useColorModeValue } from 'toolkit/chakra/color-mode';
import { Image } from 'toolkit/chakra/image';
import { Tooltip } from 'toolkit/chakra/tooltip';
import { getIconProps } from 'ui/shared/entities/base/utils';
import * as TokenEntity from 'ui/shared/entities/token/TokenEntity';
import TokenLogoPlaceholder from 'ui/shared/TokenLogoPlaceholder';

import type { TokenVerifiedDisplay } from './useTokenVerifiedDisplay';

interface Props {
  token: TokenInfo;
  isLoading: boolean;
  display: TokenVerifiedDisplay | null;
  isDisplayLoading: boolean;
}

const VerifiedTokenBadge = () => {
  const borderColor = useColorModeValue('white', 'black');

  return (
    <Box
      position="absolute"
      top="-2px"
      right="-2px"
      boxSize="14px"
      borderRadius="full"
      bg="green.500"
      color="white"
      fontSize="9px"
      fontWeight={ 700 }
      display="flex"
      alignItems="center"
      justifyContent="center"
      lineHeight={ 1 }
      borderWidth="1.5px"
      borderColor={ borderColor }
      zIndex={ 1 }
    >
      V
    </Box>
  );
};

const TokenPageTitleIcon = ({ token, isLoading, display, isDisplayLoading }: Props) => {
  const networkIconSrc = useColorModeValue(
    config.UI.navigation.icon.default,
    config.UI.navigation.icon.dark || config.UI.navigation.icon.default,
  );

  const isVerified = Boolean(display);
  const isIconLoading = isLoading || isDisplayLoading;

  const displayToken = React.useMemo(() => {
    if (!display?.logoUrl || display.isNetworkIcon) {
      return token;
    }

    return {
      ...token,
      icon_url: display.logoUrl,
    };
  }, [ display, token ]);

  const iconStyles = {
    boxSize: getIconProps('heading').boxSize,
    borderRadius: token.type === 'ERC-20' ? 'full' : 'base',
    flexShrink: 0,
  };

  const iconContent = display?.isNetworkIcon ? (
    <Image
      { ...iconStyles }
      src={ networkIconSrc || undefined }
      alt={ `${ token.name || token.symbol || 'token' } logo` }
      fallback={ <TokenLogoPlaceholder { ...iconStyles }/> }
    />
  ) : (
    <TokenEntity.Icon
      token={ displayToken }
      isLoading={ isIconLoading }
      variant="heading"
      marginRight={ 0 }
    />
  );

  const icon = (
    <Box position="relative" display="inline-flex" flexShrink={ 0 } mr={ 2 }>
      { iconContent }
      { isVerified && !isIconLoading && <VerifiedTokenBadge/> }
    </Box>
  );

  if (!isVerified) {
    return icon;
  }

  return (
    <Tooltip content={ display?.tooltip }>
      { icon }
    </Tooltip>
  );
};

export default React.memo(TokenPageTitleIcon);
